import { useState, useCallback, useMemo, useEffect } from "react";
import { FormFieldValue, FormDataType } from "../components/Form/FormContext";

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: FormFieldValue, formData?: FormDataType) => string | null;
  message?: string;
}

export interface FieldValidation {
  [fieldName: string]: ValidationRule;
}

export type FormData = FormDataType;

export interface ValidationResult {
  isValid: boolean;
  errors: { [fieldName: string]: string };
  errorMessages: string[];
  hasFieldError: (fieldName: string) => boolean;
}

export const useFormValidation = (
  validationRules: FieldValidation,
  initialData?: FormData,
  externalErrors?: { [fieldName: string]: string[] | string }
) => {
  const [formData, setFormData] = useState<FormData>(initialData || {});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const validateField = useCallback(
    (
      fieldName: string,
      value: FormFieldValue,
      isTouched: boolean = false
    ): string | null => {
      const rules = validationRules[fieldName];
      if (!rules) return null;

      // Allow validation when explicitly requested (e.g., on submit),
      // otherwise only validate after submit state is set
      if (!isSubmitted && !isTouched) return null;

      if (rules.required) {
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (typeof value === "string" && value.trim() === "")
        ) {
          return rules.message || `${fieldName} is required`;
        }
      }

      // Skip remaining validations only for empty strings, not for boolean false
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "")
      ) {
        // Don't skip custom validation for boolean fields
        if (typeof value !== "boolean" && !rules.custom) {
          return null;
        }
      }

      if (
        rules.minLength &&
        typeof value === "string" &&
        value.length < rules.minLength
      ) {
        return (
          rules.message ||
          `${fieldName} must be at least ${rules.minLength} characters`
        );
      }

      if (
        rules.maxLength &&
        typeof value === "string" &&
        value.length > rules.maxLength
      ) {
        return (
          rules.message ||
          `${fieldName} must be no more than ${rules.maxLength} characters`
        );
      }

      if (
        rules.pattern &&
        typeof value === "string" &&
        !rules.pattern.test(value)
      ) {
        return rules.message || `${fieldName} format is invalid`;
      }

      if (rules.custom) {
        return rules.custom(value, formData);
      }

      return null;
    },
    [validationRules, formData, isSubmitted]
  );

  const validateForm = useCallback((): ValidationResult => {
    const errors: { [fieldName: string]: string } = {};
    const errorMessages: string[] = [];

    // Always validate when validateForm is called directly
    Object.keys(validationRules).forEach((fieldName) => {
      const value = formData[fieldName];
      const error = validateField(fieldName, value, true);
      if (error) {
        errors[fieldName] = error;
        errorMessages.push(error);
      }
    });

    // Merge external (server-side) errors by field, if provided
    if (externalErrors) {
      Object.entries(externalErrors).forEach(([fieldName, fieldErrors]) => {
        const firstError = Array.isArray(fieldErrors)
          ? fieldErrors.find((e) => !!e) || null
          : fieldErrors || null;
        if (firstError) {
          errors[fieldName] = firstError as string;
        }
        // Collect all messages for display
        if (Array.isArray(fieldErrors)) {
          fieldErrors.filter(Boolean).forEach((msg) => errorMessages.push(msg));
        } else if (fieldErrors) {
          errorMessages.push(fieldErrors as string);
        }
      });
    }

    return {
      isValid: errorMessages.length === 0,
      errors,
      errorMessages,
      hasFieldError: (fieldName: string) => !!errors[fieldName],
    };
  }, [formData, validateField, validationRules, externalErrors]);

  const updateField = useCallback(
    (fieldName: string, value: FormFieldValue) => {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
    },
    []
  );

  const touchField = useCallback((fieldName: string) => {
    setTouchedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(fieldName);
      return newSet;
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData({});
    setTouchedFields(new Set());
    setIsSubmitted(false);
  }, []);

  const submitForm = useCallback(() => {
    setIsSubmitted(true);
  }, []);

  const setFieldError = useCallback((fieldName: string, _error: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: prev[fieldName] || "" }));
  }, []);

  const validationResult = useMemo(() => {
    if (!isSubmitted) {
      return {
        isValid: true,
        errors: {},
        errorMessages: [],
        hasFieldError: () => false,
      };
    }
    return validateForm();
  }, [validateForm, isSubmitted]);

  return {
    formData,
    touchedFields,
    validationResult,
    updateField,
    touchField,
    resetForm,
    setFieldError,
    validateField,
    validateForm,
    submitForm,
    isSubmitted,
  };
};
